// Chakra imports
import {
  Box,
  SimpleGrid,
  useColorModeValue,
  Spinner,
  Flex,
  Center,
} from "@chakra-ui/react";
// Custom components
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import TotalSpent from "views/admin/default/components/TotalSpent";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import EndpointLogsTable from "views/admin/default/components/EndpointLogsTable";
import NotificationStack from "components/notification/NotificationStack";
import { useSearch } from "contexts/SearchContext";
import {
  columnsDataComplex,
} from "views/admin/default/variables/columnsData";
import tableDataComplex from "views/admin/default/variables/tableDataComplex.json";
import useEndpoints from "providers/Endpoints";
import useAuthentication from "providers/Authentication";
import constants from "providers/Constants";

export default function UserReports() {
  const { t } = useTranslation();
  const { searchQuery } = useSearch();

  const [analyticsYearly, setAnalyticsYearly] = useState([]);
  const [analyticsWeekly, setAnalyticsWeekly] = useState([]);
  const [endpointHistory, setEndpointHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadCounter, setLoadCounter] = useState(0);

  const endpoints = useEndpoints();
  const authentication = useAuthentication();

  useEffect(() => {
    getUser();
    getEndpointLogs();
    getEndpointHistory();
  }, []);

  useEffect(() => {
    if (loadCounter === 4) {
      setLoading(false);
    }
  }, [loadCounter]);

  // Chakra Color Mode
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  if (loading) {
    return (
      <Center height="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {/* Modern Notification Stack */}
      <NotificationStack
        notifications={notifications.map((message) => ({
          type: 'error',
          title: 'Action Required',
          message: message,
        }))}
      />
      <SimpleGrid columns={{ base: 1, md: 1, xl: 1 }} gap='20px' mb='20px'>
        <TotalSpent data={analyticsYearly} />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
        <DailyTraffic data={analyticsWeekly} />
        <WeeklyRevenue data={endpointHistory} />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 1 }} gap='20px' mb='20px'>
        <EndpointLogsTable
          title={t("Endpoint Logs")}
          index={(page) => { }}
          columnsData={[
            {
              Header: t("ENDPOINT"),
              accessor: "endpoint",
            },
            {
              Header: t("STATUS"),
              accessor: "status",
            },
            {
              Header: t("DATE"),
              accessor: "created_at",
            },
            {
              Header: t("TOKEN USAGE"),
              accessor: "used_token_percentage",
            },
          ]}
          tableData={logs}
          searchQuery={searchQuery}
        />
      </SimpleGrid>
    </Box>
  );

  // Get user
  async function getUser() {
    authentication.getUser((error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || "Snap! Something went wrong, Try again"
        );
      } else {
        isContactInfoValidated(data?.user || {}, data?.profile || {}, data?.developer || {});
        getTransactionAnalyticsWeekly(data?.user?.country_shortcode, 'weekly');
        getTransactionAnalyticsYearly(data?.user?.country_shortcode, 'yearly');
      }
    });
  }

  // Get transaction analytics yearly
  function getTransactionAnalyticsYearly(country_shortcode, period) {
    endpoints.getTransactionAnalytics(country_shortcode, period, false, (error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || "Snap! Something went wrong, Try again"
        );
      } else {
        setAnalyticsYearly(data);
        setLoadCounter(prev => prev + 1);
      }
    });
  }

  // Get transaction analytics weekly
  function getTransactionAnalyticsWeekly(country_shortcode, period) {
    endpoints.getTransactionAnalytics(country_shortcode, period, false, (error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || "Snap! Something went wrong, Try again"
        );
      } else {
        setAnalyticsWeekly(data);
        setLoadCounter(prev => prev + 1);
      }
    });
  }

  // Get endpoint logs
  function getEndpointLogs() {
    endpoints.getEndpointLogs((error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || "Snap! Something went wrong, Try again"
        );
      } else {
        setLogs(data);
        setLoadCounter(prev => prev + 1);
      }
    });
  }

  // Get endpoint history
  function getEndpointHistory() {
    endpoints.getEndpointHistory('weekly', (error, data) => {
      if (error) {
        constants.showToast(
          "error",
          "FAILED",
          error?.message || "Snap! Something went wrong, Try again"
        );
      } else {
        setEndpointHistory(data);
        setLoadCounter(prev => prev + 1);
      }
    });
  }

  // Validate contact info
  async function isContactInfoValidated(user, profile, developer) {
    const newNotifications = new Set(notifications);
    if (!user?.email_verified) {
      newNotifications.add(t("You need to verify your email address! Your access to some APIs might be limited"));
    }
    if (!user?.phone_verified) {
      newNotifications.add(t("You need to verify your phone number! Your access to some APIs might be limited"));
    }
    if (!profile?.document_upload_verified) {
      newNotifications.add(t("You need to submit your identity document(s) for verification! Your access to some APIs might be limited"));
    }
    if (!profile?.address_upload_verified) {
      newNotifications.add(t("You need to submit your proof of address document(s) for verification! Your access to some APIs might be limited"));
    }
    if (!profile?.business_upload_verified) {
      newNotifications.add(t("You need to submit your business document(s) for verification! Your access to some APIs might be limited"));
    }
    if (user?.country_shortcode === "NG" && !profile?.bvn) {
      newNotifications.add(t("A Bank Verification Number (BVN) is required for Nigerian developers, Kindly update your profile"));
    }
    if (user?.country_shortcode === "NG" && !profile?.bvn_verified) {
      newNotifications.add(t("Your Bank Verification Number (BVN) is not verified. Kindly update your profile"));
    }
    if (!developer?.webhook_url) {
      newNotifications.add(t("You need to set a Webhook URL to receive transaction notifications"));
    }
    if (!developer?.enable_webhooks) {
      newNotifications.add(t("You need to enable Webhooks to receive transaction notifications"));
    }
    setNotifications([...newNotifications]);
  }
}
